import { Group, Text, Badge, Tooltip } from "@mantine/core";
import {
  IconActivity,
  IconShare,
  IconLink,
  IconGripVertical,
  IconCornerDownRight,
} from "@tabler/icons-react";
import { Link } from "react-router-dom";
import {
  TableActions,
  TableAction,
  EditAction,
  DeleteAction,
  actionsColumn,
} from "../TableActions";
import { DataTable } from "../DataTable";
import { useDeletePoll, useReorderPolls } from "../../api/polls";
import { Ionicon } from "./../Ionicon";
import SharePollResultsModal from "./SharePollResultsModal";
import { useState, useMemo } from "react";
import { useDeleteConfirm } from "../../hooks/useDeleteConfirm";
import { DataTableDraggableRow } from "mantine-datatable";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

export default function PollsTable({ data, isLoading }) {
  const { mutate: deletePoll } = useDeletePoll();
  const confirmDelete = useDeleteConfirm("poll", deletePoll);
  const confirmDeleteWithFollowUp = useDeleteConfirm("poll", deletePoll, {
    message:
      "This question has a follow up which will also be deleted. Are you sure?",
  });
  const { mutate: reorderPolls } = useReorderPolls();
  const [shareModalPollId, setShareModalPollId] = useState(null);

  const [reorderedPolls, setReorderedPolls] = useState(
    flattenPolls(data?.data)
  );

  useMemo(() => {
    setReorderedPolls(flattenPolls(data?.data));
  }, [data?.data]);

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;

    const mainPolls = reorderedPolls.filter((p) => !p.isFollowUp);
    const followUpPolls = reorderedPolls.filter((p) => p.isFollowUp);

    const items = Array.from(mainPolls);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);

    const newOrder = [];
    for (const mainPoll of items) {
      newOrder.push(mainPoll);
      let current = mainPoll;
      while (current?.followUpPollId) {
        const followUpId = current.followUpPollId;
        const followUp = followUpPolls.find((p) => p.id === followUpId);
        if (followUp) {
          newOrder.push(followUp);
          current = followUp;
        } else {
          break;
        }
      }
    }

    setReorderedPolls(newOrder);

    const eventId = items[0]?.eventId;
    const sessionId = items[0]?.sessionId;

    if (!eventId || !sessionId) {
      setReorderedPolls(flattenPolls(data?.data));
      return;
    }

    reorderPolls(
      {
        pollIds: items.map((poll) => poll.id),
        eventId,
        sessionId,
      },
      {
        onError: () => setReorderedPolls(flattenPolls(data?.data)),
      }
    );
  };

  return (
    <>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="polls-table">
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              style={{ width: "100%" }}
            >
              <DataTable
                records={reorderedPolls}
                columns={[
                  {
                    accessor: "",
                    width: 40,
                    hiddenContent: true,
                    render: (poll) =>
                      poll.isFollowUp ? poll.isFollowUp : null,
                  },
                  {
                    accessor: "id",
                    render: ({ id, isFollowUp }) => (
                      <Text
                        size="sm"
                        fw={500}
                        c={isFollowUp ? "dimmed" : undefined}
                      >
                        #{id}
                      </Text>
                    ),
                  },
                  {
                    accessor: "sessionTitle",
                    render: ({ sessionTitle, isFollowUp }) => (
                      <Text size="sm" c={isFollowUp ? "dimmed" : undefined}>
                        {sessionTitle}
                      </Text>
                    ),
                  },
                  {
                    accessor: "title",
                    title: "Question",
                    width: 300,
                    render: (poll) => {
                      const indentSize = poll.depth * 30;

                      return (
                        <Group
                          gap="xs"
                          wrap="nowrap"
                          style={{ paddingLeft: indentSize }}
                        >
                          {poll.isFollowUp && (
                            <IconCornerDownRight size={16} color="#868e96" />
                          )}
                          <Group style={styles.wrapper}>
                            <Text size="sm">{poll?.title}</Text>
                            {poll?.isVisible && (
                              <Badge variant="dot" color="green">
                                Visible
                              </Badge>
                            )}
                          </Group>
                        </Group>
                      );
                    },
                  },
                  {
                    accessor: "description",
                    render: ({ description, isFollowUp }) => (
                      <Text size="sm" c={isFollowUp ? "dimmed" : undefined}>
                        {description}
                      </Text>
                    ),
                  },
                  {
                    accessor: "status",
                    width: 100,
                    render: (poll) => (
                      <Badge
                        variant="light"
                        color={poll?.status === "open" ? "green" : "red"}
                      >
                        {poll?.status}
                      </Badge>
                    ),
                  },
                  {
                    accessor: "areResultsVisible",
                    width: 100,
                    title: (
                      <Group style={{ ...styles.wrapper, gap: 2 }}>
                        Results
                        <Tooltip
                          multiline
                          w={220}
                          label="After submitting their vote, users can see Poll results in Shared results"
                        >
                          <Group style={{ height: 18, width: 18 }}>
                            <Ionicon name="information-circle-outline" />
                          </Group>
                        </Tooltip>
                      </Group>
                    ),
                    render: (poll) => (
                      <Badge
                        variant={poll?.areResultsVisible ? "light" : "outline"}
                        color={poll?.areResultsVisible ? "orange" : "gray"}
                      >
                        {poll?.areResultsVisible ? "Shared" : "Hidden"}
                      </Badge>
                    ),
                  },
                  actionsColumn(
                    (poll) => (
                      <TableActions>
                        <EditAction to={`/app/polls/${poll.id}/edit`} />
                        <DeleteAction
                          onDelete={() =>
                            poll.followUpPollId
                              ? confirmDeleteWithFollowUp({ id: poll.id })
                              : confirmDelete({ id: poll.id })
                          }
                        />
                        <TableAction
                          label="Results"
                          icon={IconActivity}
                          color="green"
                          component={Link}
                          to={`/app/polls/${poll.id}/results`}
                        />
                        <TableAction
                          label="Share Results"
                          icon={poll.publicResultsToken ? IconLink : IconShare}
                          color="blue"
                          onClick={() => setShareModalPollId(poll.id)}
                        />
                      </TableActions>
                    ),
                    { width: 200 }
                  ),
                ]}
                fetching={isLoading}
                rowFactory={({ record, index, rowProps, children }) => {
                  if (record.isFollowUp) {
                    const { style, ...restRowProps } = rowProps;

                    return (
                      <tr
                        {...restRowProps}
                        style={{
                          backgroundColor: "#f8f9fa",
                        }}
                      >
                        <td></td>
                        {children}
                      </tr>
                    );
                  }

                  const mainPollIndex = reorderedPolls
                    .slice(0, reorderedPolls.indexOf(record))
                    .filter((p) => !p.isFollowUp).length;

                  return (
                    <Draggable
                      key={record.id}
                      draggableId={record.id.toString()}
                      index={mainPollIndex}
                    >
                      {(provided, snapshot) => (
                        <DataTableDraggableRow
                          isDragging={snapshot.isDragging}
                          {...rowProps}
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                        >
                          <td {...provided.dragHandleProps}>
                            <IconGripVertical size={16} />
                          </td>
                          {children}
                        </DataTableDraggableRow>
                      )}
                    </Draggable>
                  );
                }}
              />
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <SharePollResultsModal
        pollId={shareModalPollId}
        opened={!!shareModalPollId}
        onClose={() => setShareModalPollId(null)}
      />
    </>
  );
}

function flattenPolls(allPolls) {
  const followUpIds = new Set();
  for (const poll of allPolls || []) {
    if (poll.followUpPollId) {
      followUpIds.add(poll.followUpPollId);
    }
  }

  const mainPolls = (allPolls || []).filter(
    (poll) => !followUpIds.has(poll.id)
  );
  const flattened = [];

  for (const mainPoll of mainPolls) {
    flattened.push({ ...mainPoll, depth: 0, isFollowUp: false });

    let currentPoll = mainPoll;
    let depth = 1;

    while (currentPoll?.followUpPollId) {
      const followUpPollId = currentPoll.followUpPollId;
      const nextPoll = allPolls.find((p) => p.id === followUpPollId);

      if (nextPoll) {
        flattened.push({ ...nextPoll, depth, isFollowUp: true });
        currentPoll = nextPoll;
        depth++;
      } else {
        break;
      }
    }
  }

  return flattened;
}

const styles = {
  wrapper: {
    display: "flex",
    gap: 10,
    alignItems: "center",
  },
};
